import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Layout from '../components/Layout';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { Plus, MagnifyingGlass, Phone, EnvelopeSimple, Star, PencilSimple, Trash, ArrowCounterClockwise, ArrowLeft, CaretRight, CaretDown } from '@phosphor-icons/react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export default function Vendors() {
  const { t } = useLanguage();
  const { isAdmin } = useAuth();
  const navigate = useNavigate();
  const [vendors, setVendors] = useState([]);
  const [search, setSearch] = useState('');
  const [archiveFilter, setArchiveFilter] = useState('active');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editingVendor, setEditingVendor] = useState(null);
  const [deletingVendor, setDeletingVendor] = useState(null);
  const [form, setForm] = useState({ name: '', phone: '', email: '', notes: '' });
  // Phase 724 — inline expandable rows: expensesByVendor caches per-vendor
  // expense lists so re-expanding is instant. loadingVendor tracks the fetch
  // in progress.
  const [expandedVendors, setExpandedVendors] = useState({}); // vendor_id -> bool
  const [expensesByVendor, setExpensesByVendor] = useState({}); // vendor_id -> array
  const [loadingVendor, setLoadingVendor] = useState(null); // vendor_id being fetched

  const fetchVendors = useCallback(async (af) => {
    try {
      const res = await axios.get(`${API}/vendors?archive_filter=${af || archiveFilter}`);
      setVendors(res.data);
    } catch (err) { console.error(err); }
  }, [archiveFilter]);

  useEffect(() => { fetchVendors(); }, [fetchVendors]);

  const handleArchiveFilter = (val) => { setArchiveFilter(val); fetchVendors(val); };

  // Phase 724 — Toggle inline expansion. Fetches the vendor's expenses the
  // first time it's opened, then caches them locally so subsequent toggles
  // are instant. Uses the existing GET /vendors/{id} endpoint which already
  // returns expenses grouped by building.
  const toggleExpand = useCallback(async (vendorId) => {
    setExpandedVendors(prev => {
      const next = { ...prev, [vendorId]: !prev[vendorId] };
      return next;
    });
    if (!expensesByVendor[vendorId]) {
      setLoadingVendor(vendorId);
      try {
        const res = await axios.get(`${API}/vendors/${vendorId}`);
        // Flatten expenses_by_building into a single sorted list.
        const flat = [];
        (res.data.expenses_by_building || []).forEach(bld => {
          (bld.expenses || []).forEach(exp => {
            flat.push({ ...exp, building_name: bld.building_name });
          });
        });
        flat.sort((a, b) => (b.date || '').localeCompare(a.date || ''));
        setExpensesByVendor(prev => ({ ...prev, [vendorId]: flat }));
      } catch (err) {
        toast.error(err.response?.data?.detail || t('errorOccurred'));
      } finally {
        setLoadingVendor(null);
      }
    }
  }, [expensesByVendor, t]);

  const filtered = vendors.filter(v =>
    v.name.toLowerCase().includes(search.toLowerCase()) ||
    (v.phone || '').includes(search) ||
    (v.email || '').toLowerCase().includes(search.toLowerCase())
  );

  const openCreate = () => {
    setEditingVendor(null);
    setForm({ name: '', phone: '', email: '', notes: '' });
    setDialogOpen(true);
  };

  const openEdit = (v) => {
    setEditingVendor(v);
    setForm({ name: v.name, phone: v.phone || '', email: v.email || '', notes: v.notes || '' });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingVendor) {
        await axios.put(`${API}/vendors/${editingVendor.vendor_id}`, form);
      } else {
        await axios.post(`${API}/vendors`, form);
      }
      toast.success(t('savedSuccess'));
      setDialogOpen(false);
      fetchVendors();
    } catch (err) { toast.error(err.response?.data?.detail || t('errorOccurred')); }
  };

  const handleDelete = async () => {
    if (!deletingVendor) return;
    try {
      await axios.delete(`${API}/vendors/${deletingVendor.vendor_id}`);
      toast.success(t('deleteSuccess'));
      setDeleteDialogOpen(false);
      setDeletingVendor(null);
      fetchVendors();
    } catch (err) { toast.error(err.response?.data?.detail || t('errorOccurred')); }
  };

  return (
    <Layout>
      <div className="max-w-6xl mx-auto p-6 space-y-6">
        <button
          onClick={() => navigate('/building-settings')}
          className="flex items-center gap-1.5 text-sm text-zinc-500 hover:text-zinc-900"
          data-testid="vendors-back-to-settings"
        >
          <ArrowLeft size={14} />
          <span className="font-semibold">{t('settings') || 'Settings'}</span>
        </button>
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold text-zinc-900" data-testid="vendors-title">{t('vendors')}</h1>
          {isAdmin && (
            <Button onClick={openCreate} className="bg-zinc-950 rounded-none" data-testid="add-vendor-btn">
              <Plus size={16} className="mr-2" />{t('addVendor')}
            </Button>
          )}
        </div>

        {/* Search + Archive Filter */}
        <div className="flex items-center gap-4">
          <div className="relative flex-1 max-w-md">
            <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
            <Input value={search} onChange={e => setSearch(e.target.value)} placeholder={t('searchVendors')} className="pl-9 rounded-none" data-testid="vendor-search" />
          </div>
          <select value={archiveFilter} onChange={(e) => handleArchiveFilter(e.target.value)}
            className="text-sm border border-zinc-200 rounded-none px-3 py-2 bg-white text-zinc-700 focus:outline-none" data-testid="vendor-archive-filter">
            <option value="active">{t('activeItems')}</option>
            <option value="archived">{t('archivedItems')}</option>
            <option value="all">{t('allItems')}</option>
          </select>
        </div>

        {/* Vendor List - Table View */}
        {filtered.length === 0 ? (
          <div className="bg-white border border-zinc-200 p-12 text-center text-zinc-500" data-testid="vendor-list">{t('noVendors')}</div>
        ) : (
          <div className="bg-white border border-zinc-200 overflow-hidden" data-testid="vendor-list">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-zinc-50 border-b border-zinc-200">
                    <th className="w-10 px-3 py-3"></th>
                    <th className="text-left px-5 py-3 text-xs font-semibold text-zinc-500 uppercase tracking-wider">{t('vendorName')}</th>
                    <th className="text-left px-5 py-3 text-xs font-semibold text-zinc-500 uppercase tracking-wider">{t('phone')}</th>
                    <th className="text-left px-5 py-3 text-xs font-semibold text-zinc-500 uppercase tracking-wider">{t('email')}</th>
                    <th className="text-right px-5 py-3 text-xs font-semibold text-zinc-500 uppercase tracking-wider">{t('totalExpenses')}</th>
                    <th className="text-center px-5 py-3 text-xs font-semibold text-zinc-500 uppercase tracking-wider">{t('buildings')}</th>
                    {isAdmin && <th className="text-right px-5 py-3 text-xs font-semibold text-zinc-500 uppercase tracking-wider w-28">{t('actions')}</th>}
                  </tr>
                </thead>
                <tbody className="divide-y divide-zinc-100">
                  {filtered.map(v => {
                    const isOpen = !!expandedVendors[v.vendor_id];
                    const expList = expensesByVendor[v.vendor_id] || [];
                    const isLoading = loadingVendor === v.vendor_id;
                    // Phase 725 — separate Net / VAT / Total totals. For legacy
                    // expenses without net_amount/vat_amount, treat `amount` as
                    // gross-inclusive with zero VAT.
                    const netTotal = expList.reduce((s, e) => s + (e.net_amount != null ? e.net_amount : (e.amount || 0)), 0);
                    const vatTotal = expList.reduce((s, e) => s + (e.vat_amount || 0), 0);
                    const grandTotal = expList.reduce((s, e) => s + (e.amount || 0), 0);
                    const colspan = isAdmin ? 7 : 6;
                    return (
                    <React.Fragment key={v.vendor_id}>
                    <tr className={`hover:bg-zinc-50 transition-colors cursor-pointer ${v.is_deleted ? 'opacity-60 bg-zinc-50/50' : ''} ${isOpen ? 'bg-zinc-50' : ''}`} onClick={() => toggleExpand(v.vendor_id)} data-testid={`vendor-row-${v.vendor_id}`}>
                      <td className="w-10 px-3 py-3 text-zinc-500">
                        <button type="button" onClick={(e) => { e.stopPropagation(); toggleExpand(v.vendor_id); }} className="p-1 hover:bg-zinc-100 rounded-none" aria-label={isOpen ? 'Collapse' : 'Expand'} data-testid={`vendor-expand-${v.vendor_id}`}>
                          {isOpen ? <CaretDown size={14} weight="bold" /> : <CaretRight size={14} weight="bold" />}
                        </button>
                      </td>
                      <td className="px-5 py-3">
                        <button type="button" onClick={(e) => { e.stopPropagation(); navigate(`/vendors/${v.vendor_id}`); }} className="font-semibold text-zinc-900 hover:text-blue-700 hover:underline transition-colors text-left" data-testid={`vendor-name-${v.vendor_id}`}>{v.name}</button>
                        {v.is_deleted && <span className="ml-2 inline-flex items-center px-1.5 py-0.5 text-[10px] font-semibold bg-zinc-200 text-zinc-600 border border-zinc-300">{t('archived')}</span>}
                      </td>
                      <td className="px-5 py-3 text-zinc-600">{v.phone || '-'}</td>
                      <td className="px-5 py-3 text-zinc-600">{v.email || '-'}</td>
                      <td className="px-5 py-3 text-right font-mono">
                        <button onClick={(e) => { e.stopPropagation(); navigate(`/vendors/${v.vendor_id}`); }} className="font-semibold text-zinc-800 hover:text-blue-700 hover:underline transition-colors" data-testid={`vendor-total-${v.vendor_id}`}>
                          &euro;{(v.total_expenses || 0).toFixed(2)}
                        </button>
                      </td>
                      <td className="px-5 py-3 text-center font-mono">{v.buildings_count || 0}</td>
                      {isAdmin && (
                        <td className="px-5 py-3 text-right" onClick={e => e.stopPropagation()}>
                          <div className="flex justify-end gap-1">
                            {v.is_deleted ? (<>
                              <button onClick={async () => { try { await axios.post(`${API}/vendors/${v.vendor_id}/restore`); toast.success(t('itemRestored')); fetchVendors(); } catch { toast.error(t('errorOccurred')); }}} className="p-1.5 hover:bg-emerald-50 text-emerald-500" title={t('restore')} data-testid={`restore-vendor-${v.vendor_id}`}><ArrowCounterClockwise size={14} /></button>
                              <button onClick={async () => { if (window.confirm(t('confirmPermanentDelete'))) { try { await axios.delete(`${API}/vendors/${v.vendor_id}/permanent`); toast.success(t('itemPermanentlyDeleted')); fetchVendors(); } catch { toast.error(t('errorOccurred')); }}}} className="p-1.5 hover:bg-red-50 text-red-500" title={t('permanentDelete')} data-testid={`perm-delete-vendor-${v.vendor_id}`}><Trash size={14} /></button>
                            </>) : (<>
                              <button onClick={() => openEdit(v)} className="p-1.5 hover:bg-zinc-100" data-testid={`edit-vendor-${v.vendor_id}`}><PencilSimple size={14} /></button>
                              <button onClick={() => { setDeletingVendor(v); setDeleteDialogOpen(true); }} className="p-1.5 hover:bg-red-50 text-red-600" data-testid={`delete-vendor-${v.vendor_id}`}><Trash size={14} /></button>
                            </>)}
                          </div>
                        </td>
                      )}
                    </tr>
                    {isOpen && (
                      <tr className="bg-zinc-50/60 border-b-2 border-zinc-200" data-testid={`vendor-expanded-${v.vendor_id}`}>
                        <td></td>
                        <td colSpan={colspan - 1} className="px-5 py-4">
                          {isLoading ? (
                            <div className="text-sm text-zinc-500 py-4 text-center">{t('loading') || 'Loading…'}</div>
                          ) : expList.length === 0 ? (
                            <div className="text-sm text-zinc-500 py-4 text-center" data-testid={`vendor-no-expenses-${v.vendor_id}`}>{t('noExpenses') || 'No expenses found.'}</div>
                          ) : (
                            <div className="bg-white border border-zinc-200">
                              <table className="w-full text-xs">
                                <thead>
                                  <tr className="bg-zinc-100 border-b border-zinc-200 text-zinc-600">
                                    <th className="text-left px-3 py-2 font-semibold uppercase tracking-wider">{t('date')}</th>
                                    <th className="text-left px-3 py-2 font-semibold uppercase tracking-wider">{t('building')}</th>
                                    <th className="text-left px-3 py-2 font-semibold uppercase tracking-wider">{t('reference')}</th>
                                    <th className="text-left px-3 py-2 font-semibold uppercase tracking-wider">{t('category')}</th>
                                    <th className="text-left px-3 py-2 font-semibold uppercase tracking-wider">{t('description') || 'Description'}</th>
                                    <th className="text-right px-3 py-2 font-semibold uppercase tracking-wider">{t('netAmount') || 'Net'}</th>
                                    <th className="text-right px-3 py-2 font-semibold uppercase tracking-wider">{t('vatAmount') || 'VAT'}</th>
                                    <th className="text-right px-3 py-2 font-semibold uppercase tracking-wider">{t('totalAmount') || 'Total'}</th>
                                  </tr>
                                </thead>
                                <tbody className="divide-y divide-zinc-100">
                                  {expList.map(exp => {
                                    const net = exp.net_amount != null ? exp.net_amount : (exp.amount || 0);
                                    const vat = exp.vat_amount || 0;
                                    const tot = exp.amount || 0;
                                    return (
                                    <tr key={exp.expense_id} className="hover:bg-zinc-50">
                                      <td className="px-3 py-2 whitespace-nowrap">{exp.date ? new Date(exp.date).toLocaleDateString('en-GB') : '-'}</td>
                                      <td className="px-3 py-2 text-zinc-700">{exp.building_name || '-'}</td>
                                      <td className="px-3 py-2 font-mono text-zinc-700">{exp.reference_number || exp.reference || '-'}</td>
                                      <td className="px-3 py-2 text-zinc-700">{exp.category || exp.category_name || '-'}</td>
                                      <td className="px-3 py-2 text-zinc-600 max-w-xs truncate" title={exp.description || exp.notes || ''}>{exp.description || exp.notes || '-'}</td>
                                      <td className="px-3 py-2 text-right font-mono">&euro;{net.toFixed(2)}</td>
                                      <td className="px-3 py-2 text-right font-mono text-zinc-600">&euro;{vat.toFixed(2)}</td>
                                      <td className="px-3 py-2 text-right font-mono">&euro;{tot.toFixed(2)}</td>
                                    </tr>
                                    );
                                  })}
                                  <tr className="bg-zinc-50 font-semibold border-t-2 border-zinc-300">
                                    <td colSpan={5} className="px-3 py-2 text-right uppercase tracking-wider text-zinc-700">{t('subtotal')}</td>
                                    <td className="px-3 py-2 text-right font-mono text-zinc-900" data-testid={`vendor-net-total-${v.vendor_id}`}>&euro;{netTotal.toFixed(2)}</td>
                                    <td className="px-3 py-2 text-right font-mono text-zinc-900" data-testid={`vendor-vat-total-${v.vendor_id}`}>&euro;{vatTotal.toFixed(2)}</td>
                                    <td className="px-3 py-2 text-right font-mono text-zinc-900" data-testid={`vendor-grand-total-${v.vendor_id}`}>&euro;{grandTotal.toFixed(2)}</td>
                                  </tr>
                                </tbody>
                              </table>
                            </div>
                          )}
                        </td>
                      </tr>
                    )}
                    </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Create/Edit Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="sm:max-w-md rounded-none">
            <DialogHeader><DialogTitle>{editingVendor ? t('editVendor') : t('addVendor')}</DialogTitle></DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div><Label>{t('vendorName')} *</Label><Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} required className="rounded-none mt-1" data-testid="vendor-name-input" /></div>
              <div><Label>{t('phone')}</Label><Input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} className="rounded-none mt-1" data-testid="vendor-phone-input" /></div>
              <div><Label>{t('email')}</Label><Input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} className="rounded-none mt-1" data-testid="vendor-email-input" /></div>
              <div><Label>{t('notes')}</Label><textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} className="w-full border border-zinc-200 p-2 text-sm min-h-[60px] mt-1" data-testid="vendor-notes-input" /></div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="rounded-none">{t('cancel')}</Button>
                <Button type="submit" className="bg-zinc-950 rounded-none" data-testid="vendor-submit-btn">{t('save')}</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation */}
        <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <AlertDialogContent className="rounded-none">
            <AlertDialogHeader>
              <AlertDialogTitle>{t('confirmDelete')}</AlertDialogTitle>
              <AlertDialogDescription>{t('deleteVendorWarning')}: <strong>{deletingVendor?.name}</strong></AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="rounded-none">{t('cancel')}</AlertDialogCancel>
              <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700 rounded-none" data-testid="confirm-delete-vendor">{t('delete')}</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </Layout>
  );
}
